<template>
  <button
    ref="btn"
    class="vs-component vs-button"
    :class="[
      `vs-button-${isColor ? color : null}`,
      `vs-button-${type}`,
      $attrs.class,
      {
        isActive,
        includeIcon: icon,
        includeIconOnly: icon && !hasDefaultSlot,
        'vs-radius': radius,
      },
      size,
    ]"
    :style="[
      styles,
      $attrs.style,
      {
        width: hasPixelSize ? size : null,
        height: hasPixelSize ? size : null,
      },
    ]"
    :type="button"
    name="button"
    v-bind="buttonAttrs"
    @click="clickButton"
    @blur="blurButton"
    @mouseover="mouseoverButton"
    @mouseout="mouseoutButton"
  >
    <span
      v-if="!is('line') && !is('gradient') && !is('relief')"
      ref="backgroundx"
      class="vs-button-backgroundx vs-button--background"
      :style="stylesBackground"
    />
    <vs-icon
      v-if="icon"
      class="vs-button--icon"
      :style="iconStyle"
      :icon-pack="iconPack"
      :icon="icon"
    />
    <span v-if="hasDefaultSlot" class="vs-button-text vs-button--text">
      <slot />
    </span>
    <span ref="linex" class="vs-button-linex" :style="styleLine" />
  </button>
</template>

<script>
const VUESAX_COLORS = [
  "primary",
  "secondary",
  "success",
  "danger",
  "warning",
  "dark",
  "light",
];

function hexToRgb(color) {
  const normalized = color.replace(
    /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
    (_, red, green, blue) => red + red + green + green + blue + blue
  );
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    normalized
  );
  return result
    ? {
        red: parseInt(result[1], 16),
        green: parseInt(result[2], 16),
        blue: parseInt(result[3], 16),
      }
    : null;
}

function getColor(color, opacity = 1) {
  if (!color) return null;
  if (color.startsWith("#")) {
    const rgb = hexToRgb(color);
    if (!rgb) return color;
    return opacity === 1
      ? `rgb(${rgb.red},${rgb.green},${rgb.blue})`
      : `rgba(${rgb.red},${rgb.green},${rgb.blue},${opacity})`;
  }
  if (color.startsWith("rgb(") && opacity !== 1) {
    return color.replace(/^rgb\(/, "rgba(").replace(/\)$/, `,${opacity})`);
  }
  return color;
}

function darken(color, percent) {
  const rgb = color && color.match(/\d+/g);
  if (!rgb || rgb.length < 3) return color;
  const amount = Math.abs(percent);
  const target = percent < 0 ? 0 : 255;
  return `rgb(${rgb
    .slice(0, 3)
    .map((channel) =>
      Math.round((target - Number(channel)) * amount + Number(channel))
    )
    .join(",")})`;
}

export default {
  name: "VsButton",
  inheritAttrs: false,
  props: {
    type: {
      type: String,
      default: "filled",
    },
    color: {
      type: String,
      default: "primary",
    },
    textColor: {
      type: String,
      default: null,
    },
    lineOrigin: {
      type: String,
      default: "center",
    },
    linePosition: {
      type: String,
      default: "bottom",
    },
    gradientDirection: {
      type: String,
      default: "30deg",
    },
    gradientColorSecondary: {
      type: String,
      default: "primary",
    },
    size: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: null,
    },
    iconPack: {
      type: String,
      default: "material-icons",
    },
    iconAfter: {
      type: Boolean,
      default: false,
    },
    radius: {
      type: Boolean,
      default: false,
    },
    to: {
      type: [String, Object],
      // Vuesax uses false as the disabled navigation sentinel.
      // eslint-disable-next-line vue/require-valid-default-prop
      default: false,
    },
    href: {
      type: [String, Object],
      default: "",
    },
    target: {
      type: [Boolean, String],
      default: false,
    },
    button: {
      type: String,
      default: "button",
    },
  },
  emits: ["click", "blur", "mouseover", "mouseout", "routeErr"],
  data() {
    return {
      isActive: false,
      hoverx: false,
      leftBackground: 20,
      topBackground: 20,
      radio: 0,
      time: 0.3,
      timeOpacity: 0.3,
      opacity: 1,
    };
  },
  computed: {
    hasDefaultSlot() {
      return Boolean(this.$slots.default);
    },
    isColor() {
      return VUESAX_COLORS.includes(this.color);
    },
    hasPixelSize() {
      return Boolean(this.size && /px/.test(this.size));
    },
    buttonAttrs() {
      const attrs = { ...this.$attrs };
      [
        "class",
        "style",
        "onClick",
        "onBlur",
        "onMouseover",
        "onMouseout",
      ].forEach((key) => delete attrs[key]);
      return attrs;
    },
    styles() {
      if (this.is("filled")) {
        return {
          color: getColor(this.textColor),
          background: getColor(this.color),
          boxShadow: this.hoverx
            ? `0px 8px 25px -8px ${getColor(this.color)}`
            : null,
        };
      }
      if (this.is("border") || this.is("flat")) {
        return {
          border: `${this.is("flat") ? 0 : 1}px solid ${getColor(this.color)}`,
          background: this.hoverx ? getColor(this.color, 0.1) : "transparent",
          color: getColor(this.textColor) || getColor(this.color),
        };
      }
      if (this.is("line")) {
        return {
          color: getColor(this.textColor) || getColor(this.color),
          borderBottomWidth: this.linePosition === "bottom" ? "2px" : null,
          borderColor: getColor(this.color, 0.2),
          borderTopWidth: this.linePosition === "top" ? "2px" : null,
        };
      }
      if (this.is("gradient")) {
        return {
          background: `linear-gradient(${this.gradientDirection}, ${getColor(
            this.color
          )} 0%, ${getColor(this.gradientColorSecondary)} 100%)`,
        };
      }
      if (this.is("relief")) {
        const color = getColor(this.color);
        return {
          background: color,
          boxShadow: `0 3px 0 0 ${darken(color, -0.4)}`,
        };
      }
      return {};
    },
    stylesBackground() {
      return {
        background:
          this.is("flat") || this.is("border") ? getColor(this.color) : null,
        opacity: this.opacity,
        left: `${this.leftBackground}px`,
        top: `${this.topBackground}px`,
        width: `${this.radio}px`,
        height: `${this.radio}px`,
        transition: `width ${this.time}s ease, height ${this.time}s ease, opacity ${this.timeOpacity}s ease`,
      };
    },
    styleLine() {
      let origin = "50%";
      if (this.lineOrigin === "left") origin = "0%";
      if (this.lineOrigin === "right") origin = "auto";
      return {
        top: this.linePosition === "top" ? "-2px" : "auto",
        bottom: this.linePosition === "bottom" ? "-2px" : "auto",
        background: getColor(this.color),
        left: origin,
        right: origin === "auto" ? "0px" : null,
        transform: origin === "50%" ? "translate(-50%)" : null,
      };
    },
    iconStyle() {
      const rtl = this.$vs && this.$vs.rtl;
      const left = rtl ? "left" : "right";
      const right = rtl ? "right" : "left";
      return {
        order: this.iconAfter ? 2 : 0,
        [`margin-${left}`]:
          this.hasDefaultSlot && !this.iconAfter ? "5px" : "0px",
        [`margin-${right}`]:
          this.hasDefaultSlot && this.iconAfter ? "5px" : "0px",
      };
    },
  },
  methods: {
    is(type) {
      return this.type === type;
    },
    routerPush() {
      const navigation = this.$router.push(this.to);
      if (navigation && typeof navigation.catch === "function") {
        navigation.catch((error) => this.$emit("routeErr", error));
      }
    },
    mouseoverButton(event) {
      this.$emit("mouseover", event);
      this.hoverx = true;
    },
    mouseoutButton(event) {
      this.$emit("mouseout", event);
      this.hoverx = false;
    },
    blurButton(event) {
      this.$emit("blur", event);
      this.$nextTick(() => {
        if (this.is("border") || this.is("flat")) {
          this.opacity = 0;
          setTimeout(() => {
            this.radio = 0;
          }, 150);
          this.isActive = false;
        }
      });
    },
    clickButton(event) {
      this.$emit("click", event);
      this.$nextTick(() => {
        if (this.isActive) return;
        if (this.to) this.routerPush();
        if (this.href) {
          const href = typeof this.href === "string" ? this.href : this.href.url;
          if (this.target) {
            window.open(href);
          } else {
            window.location.href = href;
          }
        }

        if (this.is("border") || this.is("flat")) this.isActive = true;

        const button = this.$refs.btn;
        if (!button) return;
        let x = event.offsetX;
        let y = event.offsetY;
        this.radio = button.clientWidth * 3;
        this.time =
          button.clientWidth /
          (button.clientWidth +
            (this.is("border") || this.is("flat") ? 70 : 20));
        if (this.is("filled")) this.timeOpacity = this.time;
        if (event.srcElement && event.srcElement !== button) {
          x += event.target.offsetLeft;
          y += event.target.offsetTop;
        }
        this.leftBackground = x;
        this.topBackground = y;
        this.opacity = this.is("filled") ? 0 : 1;

        if (this.is("filled")) {
          setTimeout(() => {
            this.time = 0;
            this.timeOpacity = 0;
            this.radio = 0;
            this.opacity = 1;
            this.isActive = false;
          }, this.time * 1100);
        } else {
          setTimeout(() => {
            this.timeOpacity = 0.15;
          }, this.time * 1100);
        }
      });
    },
  },
};
</script>
